/**
 * Variant Gallery & 3D Model Manager
 * ===================================
 * Manages dynamic product galleries and 3D model viewer integration:
 * - Switches galleries when variants change
 * - Injects 3D model iframes into variant-specific galleries
 * - Updates 3D model colors by modifying iframe src parameters
 * - Handles thumbnail active states and media selection
 * - Provides smooth scrolling to 3D viewer when thumbnail clicked
 */

(function() {
    'use strict';

    function getViewerColor(variant) {
        if (!variant?.title) return 'white';
        const title = variant.title.toLowerCase();
        if (title.includes('rose')) return 'rose';
        if (title.includes('yellow')) return 'yellow';
        if (title.includes('platinum')) return 'platinum';
        if (title.includes('white')) return 'white';
        return 'white';
    }

    const hasVariantGallery = !!document.querySelector('[data-variant-gallery]');

    if (hasVariantGallery) {
        function inject3DModel(variantGallery) {
            if (!variantGallery) return;
            document.querySelectorAll('[data-media-type="model"], [data-3d-media]').forEach(el => el.remove());
            const template = document.getElementById('template-3d-model');
            if (!template) return;
            const clone = template.content.cloneNode(true);
            const mediaList = variantGallery.querySelector('[data-product-media-list]');
            if (mediaList) mediaList.appendChild(clone);
        }

        function mark3DMedia(gallery) {
            gallery.querySelectorAll('[data-media-id$="iframe-3d"]').forEach(el => el.setAttribute('data-3d-media', ''));
        }

        // Single shared product video reused across all variant galleries.
        // Moving the same DOM node avoids re-rendering, so the video is fetched only once.
        function getSharedVideoSlide() {
            return document.querySelector('[data-shared-video]');
        }

        function moveVideoIntoGallery(gallery) {
            const videoSlide = getSharedVideoSlide();
            if (!videoSlide || !gallery) return;
            const mediaList = gallery.querySelector('[data-product-media-list]');
            if (!mediaList) return;
            // Place the video before the 360 slide if present, otherwise last.
            const viewer360 = mediaList.querySelector('[data-media-id$="iframe-3d"]');
            if (viewer360) {
                mediaList.insertBefore(videoSlide, viewer360);
            } else {
                mediaList.appendChild(videoSlide);
            }
        }

        function pauseSharedVideo(reset) {
            const video = getSharedVideoSlide()?.querySelector('video');
            if (!video) return;
            video.pause();
            if (reset) video.currentTime = 0;
        }

        function playSharedVideo() {
            const video = getSharedVideoSlide()?.querySelector('video');
            if (!video) return;
            video.muted = true;
            video.play().catch(() => {});
        }

        // This is a stacked/scroll gallery: slides sit in page flow and a thumb click
        // scrolls its slide into view. So we autoplay (muted) when the video scrolls
        // into view and stop it when it scrolls away (e.g. another image is selected).
        let videoObserver = null;
        function setupVideoObserver() {
            const slide = getSharedVideoSlide();
            if (!slide || videoObserver) return;
            videoObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.intersectionRatio >= 0.5) {
                        playSharedVideo();
                    } else if (entry.intersectionRatio <= 0.1) {
                        pauseSharedVideo(true);
                    }
                });
            }, {threshold: [0, 0.1, 0.5, 0.75, 1]});
            videoObserver.observe(slide);
        }

        function updateViewer(variant) {
            const iframes = document.querySelectorAll('.product-3d-iframe');
            if (!iframes.length) return;
            const color = getViewerColor(variant);
            iframes.forEach(iframe => {
                if (!iframe.src) return;
                const url = new URL(iframe.src);
                url.searchParams.set('part1Color', color);
                iframe.src = url.toString();
            });
        }

        window.updateActiveThumb = function(productImages, mediaId) {
            if (!productImages || !mediaId) return;
            productImages.querySelectorAll('.product__thumb.is-active').forEach(t => t.classList.remove('is-active'));
            const activeThumb = productImages.querySelector(`.product__thumb [data-media-id="${mediaId}"]`)?.closest('.product__thumb');
            activeThumb?.classList.add('is-active');
        };

        document.addEventListener('DOMContentLoaded', () => {
            const template = document.getElementById('template-3d-model');
            if (!template) return;
            document.querySelectorAll('.variant-gallery:not(.hidden)').forEach(gallery => {
                inject3DModel(gallery);
                mark3DMedia(gallery);
            });
            if (typeof selectedVariant !== 'undefined') setTimeout(() => updateViewer(selectedVariant), 1000);
        });

        // Video init runs independently of the 3D viewer (products may have a video but no 360).
        document.addEventListener('DOMContentLoaded', () => {
            const activeGallery = document.querySelector('.variant-gallery:not(.hidden)');
            if (activeGallery) moveVideoIntoGallery(activeGallery);
            setupVideoObserver();
        });

        // Autoplay (muted) when the video is selected; stop it when another media is shown.
        document.addEventListener('theme:media:select', e => {
            const id = e.detail?.id;
            if (!getSharedVideoSlide()) return;
            if (id && id.endsWith('-shared-video')) {
                playSharedVideo();
            } else {
                pauseSharedVideo(true);
            }
        });

        subscribe(theme.PUB_SUB_EVENTS.variantChange, ({
            data
        }) => {
            if (!data?.variant?.id) return;
            const variantId = String(data.variant.id);
            const galleries = document.querySelectorAll('[data-variant-gallery]');
            const activeGallery = [...galleries].find(g => g.dataset.variantId === variantId);
            if (!activeGallery) return;
            inject3DModel(activeGallery);
            mark3DMedia(activeGallery);
            pauseSharedVideo(true);
            moveVideoIntoGallery(activeGallery);
            updateViewer(data.variant);
            galleries.forEach(g => {
                const isActive = g === activeGallery;
                g.classList.toggle('hidden', !isActive);
                g.classList.toggle('is-active', isActive);
            });
            const productImages = activeGallery.closest('product-images');
            if (!productImages) return;
            const activeSlide = productImages.querySelector('.product__slide.media--active');
            if (activeSlide) productImages.style.setProperty('--height', `${activeSlide.offsetHeight}px`);
            const firstThumb = productImages.querySelector('.product__thumb [data-media-id]');
            if (firstThumb) window.updateActiveThumb(productImages, firstThumb.dataset.mediaId);
        });

        document.addEventListener('theme:media:select', e => {
            const mediaId = e.detail?.id;
            if (!mediaId?.endsWith('iframe-3d')) return;
            setTimeout(() => document.getElementById('mesh3d-container')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            }), 300);
        });

        return;
    }

    let viewerReady = false;
    let pendingVariant = null;

    function updateViewerProduct(variant) {
        const iframe = document.querySelector('.product-3d-iframe');
        if (!iframe || !iframe.contentWindow || !viewerReady) {
            pendingVariant = variant;
            return;
        }
        iframe.contentWindow.postMessage({
            parts: {
                part1: {
                    colors: {
                        tone1: getViewerColor(variant)
                    }
                }
            }
        }, '*');
    }

    document.addEventListener('DOMContentLoaded', () => {
        const iframe = document.querySelector('.product-3d-iframe');
        if (!iframe) return;
        iframe.addEventListener('load', () => {
            viewerReady = true;
            if (pendingVariant) {
                updateViewerProduct(pendingVariant);
                pendingVariant = null;
            } else if (typeof selectedVariant !== 'undefined') {
                updateViewerProduct(selectedVariant);
            }
        });
    });

    subscribe(theme.PUB_SUB_EVENTS.variantChange, ({
        data
    }) => {
        if (!data?.variant) return;
        updateViewerProduct(data.variant);
    });
})();

document.addEventListener('click', (e) => {
    const thumb = e.target.closest('.product__thumb');
    if (!thumb) return;

    const gallery = thumb.closest('.stacked_wrapper.product-images') || thumb.closest('product-images');

    if (!gallery) return;

    gallery.querySelectorAll('.product__thumb.is-active').forEach(t => t.classList.remove('is-active'));

    thumb.classList.add('is-active');

    if (thumb.classList.contains('product__thumb--360')) {
        const meshTarget = document.querySelector('#mesh3d-container') || document.querySelector('.mesh3d-container');
        if (meshTarget) {
            setTimeout(() => {
                meshTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        }
        return;
    }

    const link = thumb.querySelector('[data-media-id]');
    if (!link) return;
    const mediaId = link.getAttribute('data-media-id');
    const productImages = thumb.closest('product-images');
    if (!productImages) return;
    const targetSlide = productImages.querySelector(`.product__slide[data-media-id="${mediaId}"]`);
    if (targetSlide) {
        targetSlide.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
});